import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  getIdTokenResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import { auth, firebaseMode } from "../config/firebase";
import { ensureProfile } from "../services/repository";
import { ensureStarterPlayerState } from "../services/starterHoard";
import type { HubUser } from "../types";

type Ctx = {
  user: HubUser | null;
  loading: boolean;
  firebaseMode: boolean;
  isAdmin: boolean;
  login: (email: string, password: string, name?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  setLocalName: (name: string) => Promise<void>;
};
const C = createContext<Ctx | null>(null),
  KEY = "rw_hub_local_user_v1";
function localUser(): HubUser {
  try {
    const r = localStorage.getItem(KEY);
    if (r) return JSON.parse(r) as HubUser;
  } catch {}
  const u: HubUser = {
    uid: "local-builder",
    displayName: "Local Builder",
    local: true,
  };
  localStorage.setItem(KEY, JSON.stringify(u));
  return u;
}
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<HubUser | null>(() =>
    firebaseMode ? null : localUser(),
  );
  const [loading, setLoading] = useState(firebaseMode);
  const [adminClaim, setAdminClaim] = useState(!firebaseMode);
  useEffect(() => {
    if (!auth) return;
    const timeout = window.setTimeout(() => setLoading(false), 8000);
    const unsubscribe = onAuthStateChanged(
      auth,
      async (u) => {
        window.clearTimeout(timeout);
        setAdminClaim(u ? (await getIdTokenResult(u).catch(() => null))?.claims.admin === true : false);
        const next = u
          ? {
              uid: u.uid,
              displayName: u.displayName || u.email?.split("@")[0] || "Builder",
              email: u.email,
              photoURL: u.photoURL,
              local: false,
            }
          : null;
        setUser(next);
        if (next)
          void Promise.all([
            ensureProfile(next),
            ensureStarterPlayerState(next.uid),
          ]).catch((error) =>
            console.error("Could not initialize the player account.", error),
          );
        setLoading(false);
      },
      (error) => {
        window.clearTimeout(timeout);
        console.error("Firebase authentication observer failed.", error);
        setLoading(false);
      },
    );
    return () => {
      window.clearTimeout(timeout);
      unsubscribe();
    };
  }, []);
  const value = useMemo<Ctx>(
    () => ({
      user,
      loading,
      firebaseMode,
      isAdmin:
        adminClaim || (!firebaseMode && user?.uid === "local-builder"),
      login: async (email, password, name) => {
        if (!auth) {
          const u = {
            ...localUser(),
            displayName: name?.trim() || email.trim() || "Local Builder",
          };
          localStorage.setItem(KEY, JSON.stringify(u));
          setUser(u);
          return;
        }
        await signInWithEmailAndPassword(auth, email, password);
      },
      loginWithGoogle: async () => {
        if (!auth) {
          setUser(localUser());
          return;
        }
        const credential = await signInWithPopup(auth, new GoogleAuthProvider());
        const next: HubUser = {
          uid: credential.user.uid,
          displayName:
            credential.user.displayName ||
            credential.user.email?.split("@")[0] ||
            "Builder",
          email: credential.user.email,
          photoURL: credential.user.photoURL,
          local: false,
        };
        await ensureProfile(next);
        await ensureStarterPlayerState(next.uid);
        setUser(next);
      },
      register: async (email, password, name) => {
        if (!auth) {
          const u: HubUser = {
            uid: "local-builder",
            displayName: name.trim() || "Local Builder",
            email,
            local: true,
          };
          localStorage.setItem(KEY, JSON.stringify(u));
          setUser(u);
          return;
        }
        const c = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(c.user, { displayName: name });
        await ensureStarterPlayerState(c.user.uid);
        setUser({
          uid: c.user.uid,
          displayName: name,
          email: c.user.email,
          local: false,
        });
      },
      logout: async () => {
        if (auth) await signOut(auth);
      },
      setLocalName: async (name) => {
        const displayName = name.trim() || "Builder";
        if (auth?.currentUser) {
          await updateProfile(auth.currentUser, { displayName });
          setUser((current) => current ? { ...current, displayName } : current);
          return;
        }
        const u = { ...localUser(), displayName };
        localStorage.setItem(KEY, JSON.stringify(u));
        setUser(u);
      },
    }),
    [user, loading, adminClaim],
  );
  return <C.Provider value={value}>{children}</C.Provider>;
}
export function useAuth() {
  const v = useContext(C);
  if (!v) throw new Error("AuthProvider missing");
  return v;
}
